import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** Shared state for the floating Messages drawer + header bell.
 *  - openPeer: {user_id, name, picture} | null  (drawer chat target)
 *  - unread:   total unread DM count across all threads
 *  Polled every 20s while logged-in. */
const MessagesContext = createContext({
  unread: 0,
  openPeer: null,
  drawerOpen: false,
  openWith: () => {},
  closeDrawer: () => {},
  refreshUnread: async () => {},
});

export function MessagesProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [openPeer, setOpenPeer] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const timerRef = useRef(null);

  const refreshUnread = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try {
      const { data } = await api.get("/dm/unread_count");
      setUnread(Number(data?.unread || 0));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0); setOpenPeer(null); setDrawerOpen(false);
      clearInterval(timerRef.current);
      return;
    }
    refreshUnread();
    timerRef.current = setInterval(refreshUnread, 20000);
    return () => clearInterval(timerRef.current);
  }, [user, refreshUnread]);

  const openWith = (peer) => {
    setOpenPeer(peer);
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <MessagesContext.Provider value={{ unread, openPeer, drawerOpen, openWith, closeDrawer, refreshUnread }}>
      {children}
    </MessagesContext.Provider>
  );
}

export const useMessages = () => useContext(MessagesContext);
